import * as React from 'react';
import { object as PropObject } from 'prop-types';
import { FormGroup } from 'react-bootstrap';

import { humanize, Permission, ProgramTypes, User, UserModel } from '../../gs-core/common';

import './PermissionsSection.css';
import {
  ActionTypes, Doc, PermissionEvaluator, Subject, SubjectTypes, ViewAccess, ViewAccessTypes
} from '../../common/models';
import { ServiceRegistry } from '../../services';
import { Section } from './Section';

const OWNING_UNIT = ViewAccessTypes.OWNING_UNIT;
const SUBJECTS_GROUPS = ViewAccessTypes.SUBJECTS_GROUPS;
const SUBJECTS_GROUPS_AND_SUBJECT = ViewAccessTypes.SUBJECTS_GROUPS_AND_SUBJECT;

interface PermissionsSectionProps {
  currentUser: User;
  permissions: Permission[];
  doc: Doc;
  editing: boolean;
  onChangeViewAccess: (viewAccess: ViewAccess) => void;
}

interface PermissionsSectionState { viewAccessOptions: ViewAccessOption[]; }

interface ViewAccessOption {
  whoCanView: (subject: Subject) => string[];
  value: ViewAccess;
  selected?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

const VIEW_ACCESS_OPTIONS: ViewAccessOption[] = [
  {
    whoCanView: () => [ 'Graduate Studies' ],
    value: OWNING_UNIT,
    disabled: true
  },
  {
    whoCanView: (subject: Subject) => {
      const futureUsers = subject.type === SubjectTypes.POSTDOC ?
        [ 'and future departments' ] :
        ( subject.type === SubjectTypes.STUDENT ? 'and future programs' : [] );

      return subject.groups
        .map(group => {
          const programType = group.programType;
          let suffix = '';

          if (programType) {
            suffix = programType === ProgramTypes.DESIGNATED_EMPHASIS ? ' ( DE )' : ` ( ${group.code} )`;
          } else if (group.code) {
            suffix = ` ( ${humanize(group.code)} )`;
          }

          return `${group.displayName || group.name}${suffix}`;
        })
        .concat(futureUsers);
    },
    value: SUBJECTS_GROUPS
  },
  {
    whoCanView: (subject: Subject) => [ subject.user.displayName || subject.user.name ],
    value: SUBJECTS_GROUPS_AND_SUBJECT,
    tooltip: 'Type "ctrl shift a"'
  }
];

export class PermissionsSection extends React.Component<PermissionsSectionProps, PermissionsSectionState> {
  static contextTypes = { services: PropObject };

  constructor(props: PermissionsSectionProps) {
    super(props);
    this.state = { viewAccessOptions: [] };
  }

  async componentWillReceiveProps({ doc, currentUser, permissions }: PermissionsSectionProps) {
    const { errorLogger } = this.context.services as ServiceRegistry;

    try {
      if (doc.sameAccess(this.props.doc)) {
        return;
      }

      if (!currentUser.isUnder) {
        throw new Error('Current user must be a UserModel');
      }

      const subjectsGroups = ((doc.subject && doc.subject.groups) || []) as UserModel[];

      const permissionEvaluator = PermissionEvaluator.forUser(currentUser)
        .withPermissions(permissions);

      // If the user can assign to any unit under a scope group, allow them to limit view to the doc's owning unit
      const canLimitGroupViewAccess = permissionEvaluator
        .findScopeGroups(ActionTypes.ASSIGN)
        .some(scopeGroup =>
          subjectsGroups.some(subjectsGroup => subjectsGroup.isUnder(scopeGroup))
        );

      const validViewAccessTypes = ViewAccessTypes.filterBySubject(doc.subject);

      const selectedIndex = VIEW_ACCESS_OPTIONS.findIndex(option => option.value === doc.viewAccess);
      const viewAccessOptions = VIEW_ACCESS_OPTIONS
        .map((option, i) => {
          const disabled = (option.value === SUBJECTS_GROUPS && !canLimitGroupViewAccess) || option.disabled;
          const selected = disabled || selectedIndex >= i;

          return { ...option, selected, disabled};
        })
        .filter(option => validViewAccessTypes.includes(option.value));

      this.setState({ viewAccessOptions });
    } catch (err) {
      await errorLogger.log(err);
    }
  }

  changeViewAccess = (choice: ViewAccessOption) => {
    // If it is already selected, deselect it by finding the previous option
    if (choice.selected && !choice.disabled) {
      const { viewAccessOptions } = this.state;
      const i = viewAccessOptions.findIndex(option => option.value === choice.value);

      choice = viewAccessOptions[i - 1];
    }

    this.props.onChangeViewAccess(choice.value);
  };

  render() {
    const { viewAccessOptions } = this.state;
    const { editing, doc } = this.props;

    return (
      <Section title="View Access" className={'PermissionsSection ' + ( editing ? 'editing' : 'not-editing')}>
        <FormGroup controlId="viewAccess">
          <ul className="list-unstyled">
            {viewAccessOptions.map(option => {
              const { value, selected, disabled } = option;

              return (
                <li
                  key={value}
                  className={'PermissionsSection__item checkbox ' + (editing && disabled ? 'disabled' : '')}
                >
                  <label title={editing ? option.tooltip : ''}>
                    {editing ?
                      (
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={disabled}
                          className="PermissionsSection__checkbox"
                          onChange={() => this.changeViewAccess(option)}
                        />
                      ) :
                      (
                        <i
                          className={'PermissionsSection__checkbox fa fa-lg ' +
                          (selected ? 'fa-check-square-o text-primary' : 'fa-square-o text-muted')}
                        />
                      )
                    }

                    <ul className="list-unstyled PermissionsSection__label">
                      {option.whoCanView(doc.subject as Subject)
                        .map(name => (<li className="list-unstyled" key={name}>{name}</li>))
                      }
                    </ul>
                  </label>
                </li>
              );
            })}
          </ul>
        </FormGroup>
      </Section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/DocView/Section/PermissionsSection.tsx